/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * RechartsWrapper.tsx
 *
 * WHY THIS EXISTS
 * ───────────────
 * Recharts ships its own copy of @types/react inside
 *   node_modules/recharts/node_modules/@types/react
 * That copy's ReactNode includes `bigint`, which your project's @types/react
 * does not recognise, causing TS2786 on every Recharts JSX element.
 *
 * FIX: Instead of importing named *Props types (which vary by Recharts version),
 * we infer props directly from the component using React.ComponentProps<typeof X>.
 * This works regardless of Recharts version.
 */

import React from 'react';
import * as RC from 'recharts';

// ─── helper ───────────────────────────────────────────────────────────────────
// Casts any Recharts component through React.FC so TS resolves it against
// YOUR project's @types/react, not Recharts' bundled copy.
function wrap<C extends React.ComponentType<any>>(
  component: C,
): React.FC<React.ComponentProps<C>> {
  return component as unknown as React.FC<React.ComponentProps<C>>;
}

// ─── Container ────────────────────────────────────────────────────────────────
export const ResponsiveContainer = wrap(RC.ResponsiveContainer);

// ─── Cartesian charts ─────────────────────────────────────────────────────────
export const BarChart = wrap(RC.BarChart);
export const LineChart = wrap(RC.LineChart);
export const AreaChart = wrap(RC.AreaChart);

// ─── Polar charts ─────────────────────────────────────────────────────────────
export const PieChart = wrap(RC.PieChart);

// ─── Series / shapes ─────────────────────────────────────────────────────────
export const Bar = wrap(RC.Bar as unknown as React.ComponentType<any>);
export const Line = wrap(RC.Line as React.ComponentType<any>);
export const Area = wrap(RC.Area as unknown as React.ComponentType<any>);
export const Pie = wrap(RC.Pie as unknown as React.ComponentType<any>);
export const Cell = wrap(RC.Cell as React.ComponentType<any>);

// ─── Axes ─────────────────────────────────────────────────────────────────────
export const XAxis = wrap(RC.XAxis as React.ComponentType<any>);
export const YAxis = wrap(RC.YAxis as React.ComponentType<any>);

// ─── Overlays ─────────────────────────────────────────────────────────────────
export const Tooltip = wrap(RC.Tooltip as React.ComponentType<any>);
export const Legend = wrap(RC.Legend as React.ComponentType<any>);
export const CartesianGrid = wrap(RC.CartesianGrid as React.ComponentType<any>);
export const Sector = wrap(RC.Sector as React.ComponentType<any>);
